<script>
  import { createEventDispatcher } from "svelte";
  import { Icon, BugAnt, Play, ArrowPath } from "svelte-hero-icons";
  import { execution, features, jobs } from "../store";
  import DropdownMenu from "$lib/Elements/DropdownMenu.svelte";
  import Button from "$lib/Elements/Button.svelte";
  import SelectInput from "$lib/Inputs/SelectInput.svelte";
  import BuildInput from "$lib/Inputs/BuildInput.svelte";
  import { deferred } from "$lib/utils/deferred";

  export let apps;
  export let devices;
  export let regions;
  export let availableSidecars;
  export let availableEntrypoints;
  export let selectedApp = null;
  export let selectedBuild = null;
  export let selectedDevice = null;
  export let selectedRegion = null;
  export let selectedSidecar = null;
  export let selectedEntrypoint = null;

  const dispatch = createEventDispatcher();

  $: executionMode = execution.mode;
  $: developerMode = features.developerMode;
  $: isRunning = $executionMode == "in_progress";
  $: currentJob = jobs.currentJob;

  const isReady = deferred();

  let defaultText = "Trace";
  let textDebugButton = defaultText;
  let lastSelection = null;
  let isBuildInputOpen = false;

  function getDeviceName(id) {
    return devices.find((device) => device.value == id)?.label ?? "n/a";
  }

  function debug() {
    lastSelection = "debug";
    textDebugButton = `Debug on ${getDeviceName(selectedDevice)}`;
    execution.triggerDebug(
      selectedApp,
      selectedDevice,
      selectedRegion,
      selectedSidecar,
      selectedEntrypoint,
      selectedBuild,
    );
  }

  function trace() {
    lastSelection = "trace";
    textDebugButton = `Trace on ${getDeviceName(selectedDevice)}`;
    execution.triggerTrace(
      selectedApp,
      selectedDevice,
      selectedRegion,
      selectedSidecar,
      selectedEntrypoint,
      selectedBuild,
    );
  }

  function onButtonClick(_e, { toggleDropdown }) {
    if (isRunning) {
      return execution.cancelJob($currentJob.id);
    }

    if (lastSelection === null) {
      toggleDropdown();
    }

    switch (lastSelection) {
      case "trace":
        return trace();
      case "debug":
        return debug();
    }
  }

  function resetButton() {
    textDebugButton = defaultText;
    lastSelection = null;
  }

  function isBuildAllowed(appId) {
    return apps.find((app) => app.value == appId)?.buildsAllowed ?? false;
  }

  async function onOpen() {
    dispatch("open", {});
    await isReady.promise;
  }

  function onAppChange() {
    selectedBuild = null;
  }

  async function onDeviceChange() {
    dispatch("deviceChange", {});
  }

  function onResetSidecarFilter() {
    const checkboxes = document.querySelectorAll("input[sidecar=true]");
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    selectedSidecar = [];
  }

  function onSidecarFilterChange() {
    const selectedSerialNumbers = Array.from(
      document.querySelectorAll("input[sidecar=true]:checked"),
    ).map((checkbox) => checkbox.value);

    selectedSidecar = selectedSerialNumbers;
  }

  $: if (apps !== null) isReady.resolve();
  $: isSelectionIncomplete = !selectedApp || !selectedDevice;
  $: isSelectionIncomplete && resetButton();
</script>

<DropdownMenu
  buttonText={isRunning ? "Cancel Job" : textDebugButton}
  buttonClass={isRunning ? "opacity-85" : ""}
  {onOpen}
  onClick={onButtonClick}
  hasSeperateDropdown
  disableClickOutside={isBuildInputOpen}
>
  <div slot="prefix" class="flex items-center">
    {#if isRunning}
      <Icon src={ArrowPath} class="mr-1 h-4 w-4 animate-spin" />
    {:else}
      <Icon src={lastSelection == "debug" ? BugAnt : Play} class="w-4 h-4 mr-1" />
    {/if}
  </div>

  <div slot="content" let:close>
    <h3 class="text-md font-semibold text-gray-800 mb-1">Debug Automation</h3>
    <p class="text-sm text-gray-600 mb-3">Select an App and Device to debug</p>

    <div class="mb-2">
      <SelectInput
        bind:value={selectedApp}
        on:change={onAppChange}
        optional
        label="Select an App"
        options={apps}
        variant="wide"
      />
    </div>

    <div class="mb-2">
      <BuildInput
        {selectedApp}
        isReadonly={!isBuildAllowed(selectedApp)}
        label="Select a Build"
        bind:isOpen={isBuildInputOpen}
        bind:value={selectedBuild}
      />
    </div>

    <div class="mb-2">
      <SelectInput
        bind:value={selectedDevice}
        on:change={onDeviceChange}
        optional
        label="Select a Device"
        options={devices}
        variant="wide"
      />
    </div>

    {#if developerMode && $availableSidecars.length > 0}
      <div class="flex items-center w-full pl-4 pr-6 mb-2">
        <div class="w-full text-xs border rounded px-2 py-1 bg-white">
          <div class="flex justify-between items-center mb-1">
            <div class="font-medium">Filter by Serial Numbers:</div>
            <button
              class="text-xs text-gray-500 hover:text-gray-700 focus:outline-none"
              on:click={() => onResetSidecarFilter()}
            >
              Reset
            </button>
          </div>

          {#each $availableSidecars as sidecar (sidecar.serial_number)}
            <label class="flex items-center space-x-2 hover:bg-gray-50 py-0.5">
              <input
                type="checkbox"
                sidecar="true"
                value={sidecar.serial_number}
                class="h-3 w-3"
                on:input={() => onSidecarFilterChange()}
              />
              <span>{sidecar.serial_number}</span>
            </label>
          {/each}
        </div>
      </div>
    {/if}

    <div class="mb-4">
      <SelectInput
        bind:value={selectedRegion}
        optional
        label="Teleport Region"
        trim
        options={regions}
        variant="wide"
        defaultValue="new_york_usa"
      />
    </div>

    <div class="mb-4">
      <SelectInput
        bind:value={selectedEntrypoint}
        optional
        label="Entrypoint"
        options={$availableEntrypoints}
        variant="wide"
      />
    </div>

    {#if isSelectionIncomplete}
      <p class="text-red-500 text-xs mb-4">
        Please select both an app and a device on the "Settings" page.
      </p>
    {/if}

    {#if isRunning}
      <p class="text-red-500 text-xs mb-4">Run in progress.</p>
    {/if}

    <div class="flex justify-end space-x-2">
      <Button variant="secondary" on:click={close}>Cancel</Button>

      {#if $developerMode}
        <Button
          disabled={isSelectionIncomplete || isRunning}
          icon={BugAnt}
          on:click={() => {
            debug();
            close();
          }}
        >
          Debug
        </Button>
      {/if}

      <Button
        disabled={isSelectionIncomplete || isRunning}
        icon={Play}
        on:click={() => {
          trace();
          close();
        }}
      >
        Trace
      </Button>
    </div>
  </div>
</DropdownMenu>
